import { useMutation, useQueryClient } from 'react-query'
import { useAuth0 } from '../../../contexts/auth0-context'
import callAPI from '../../../API/callSecureAPI'

export const useUpdateOrganizationFlagsMutation = () => {
    const { getTokenSilently } = useAuth0()
    const queryClient = useQueryClient()

    return useMutation(
        async ({ orgId, flags }: { orgId: string; flags: Record<string, boolean | string | null> }) => {
            const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })
            return callAPI(`/api/admin/organizations/${orgId}/flags`, 'PATCH', token, flags)
        },
        {
            onSuccess: (_, { orgId }) => {
                queryClient.invalidateQueries(['admin', 'organizations'])
                queryClient.invalidateQueries(['admin', 'organization', orgId])
            },
        }
    )
}

export const useUpdateUserRolesMutation = () => {
    const { getTokenSilently } = useAuth0()
    const queryClient = useQueryClient()

    return useMutation(
        async ({ userId, roles }: { userId: string; roles: Record<string, boolean> }) => {
            const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })
            return callAPI(`/api/admin/users/${userId}/roles`, 'PATCH', token, roles)
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['admin', 'users'])
            },
        }
    )
}

export interface NewUserPayload {
    name: string
    email: string
    readonly?: boolean
    commentingReadonly?: boolean
    roadmapIds?: string[]
}

export const useCreateOrgUsersMutation = () => {
    const { getTokenSilently } = useAuth0()
    const queryClient = useQueryClient()

    return useMutation(
        async ({ orgId, users }: { orgId: string; users: NewUserPayload[] }) => {
            const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })
            return callAPI(`/api/admin/organizations/${orgId}/users/bulk`, 'POST', token, { users })
        },
        {
            onSuccess: (_, { orgId }) => {
                queryClient.invalidateQueries(['admin', 'users', orgId])
            },
        }
    )
}

export const useExportOrgEventsMutation = () => {
    const { getTokenSilently } = useAuth0()

    return useMutation(async ({ orgId, startDate, endDate }: { orgId: string; startDate: string; endDate: string }) => {
        const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })
        const res = await fetch(
            `/api/admin/organizations/${orgId}/events/export?startDate=${startDate}&endDate=${endDate}`,
            { headers: { Authorization: `Bearer ${token}` } }
        )
        if (!res.ok) {
            const text = await res.text()
            throw new Error(text || `Export failed (${res.status})`)
        }
        return res.text()
    })
}

export const useUpdateRoadmapAllowedUsersMutation = () => {
    const { getTokenSilently } = useAuth0()
    const queryClient = useQueryClient()

    return useMutation(
        async ({ roadmapId, add, remove }: { roadmapId: string; add?: string[]; remove?: string[] }) => {
            const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })
            return callAPI(`/api/admin/roadmaps/${roadmapId}/allowed-users`, 'PATCH', token, {
                add,
                remove,
            })
        },
        {
            onSuccess: (_, { roadmapId }) => {
                queryClient.invalidateQueries(['admin', 'roadmaps'])
                queryClient.invalidateQueries(['admin', 'roadmap', roadmapId, 'allowed-users'])
            },
        }
    )
}
